import { Directive, inject, input, type OnChanges, type OnDestroy, type OnInit, type SimpleChanges } from '@angular/core';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { UiComponent } from '@df/ui/ui.component';
import type { Subscription } from 'rxjs/internal/Subscription';
import { SanitizeHtmlService } from '../../../../../../core/util/sanitize-html.service';
import { ProductLayoutComponent } from '../../product-layout.component';
import { NProductLayout } from '../../product-layout.interface';

interface IAttribute {
    attributes: (
        | {
              attribute: NProductLayout.IAttribute;
              mode: NProductLayout.EAttributeMode;
              code: string;
              values: (string | number)[];
              valuesLabels: string;
          }
        | undefined
    )[];
    bulletPoints?: boolean;
    heading: string;
}

@Directive()
export abstract class ProductLayoutDetailsGeneralAbstractComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    readonly content = input.required<NProductLayout.IContent>();
    readonly productViewLayout = input.required<ProductLayoutComponent>();

    protected readonly EMode = NProductLayout.EAttributeMode;
    protected sections: IAttribute[] = [];
    private parentViewDetectChanges?: Subscription;
    private parentViewProductChanges?: Subscription;

    private readonly attributeResource = inject(AttributeResource);
    private readonly sanitizeHtmlService = inject(SanitizeHtmlService);

    protected abstract readonly attribute: string;

    override ngOnInit() {
        this.updateData();

        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['productViewLayout']) {
            this.parentViewDetectChanges?.unsubscribe();

            this.parentViewDetectChanges = this.productViewLayout().detectChanges$.subscribe(() => this.detectChanges());

            this.parentViewProductChanges?.unsubscribe();

            this.parentViewProductChanges = this.productViewLayout().productChange?.subscribe(() => this.updateData());
        }

        super.ngOnChanges(changes);
    }

    override ngOnDestroy() {
        this.parentViewDetectChanges?.unsubscribe();
        this.parentViewProductChanges?.unsubscribe();

        super.ngOnDestroy();
    }

    protected updateData() {
        this.sections = this.getSections();

        this.detectChanges();
    }

    private getSections(): IAttribute[] {
        const sections = this.content()
            ?.[this.attribute]?.map((item: NProductLayout.IDetailsAttribute) => ({
                heading: item.heading,
                bulletPoints: item.bulletPoints,
                attributes: item.attributes.map(attribute => {
                    const code = attribute.type === 'attribute' ? attribute.code : attribute.codeManual;

                    if (!code) {
                        return undefined;
                    }

                    const values = this.getValues(code);

                    return {
                        attribute,
                        code,
                        mode: attribute.mode,
                        values: this.client.isCms ? values : values.filter(i => !!i),
                        valuesLabels: attribute.mode === NProductLayout.EAttributeMode.cms ? '' : this.getValuesLabels(values, code)
                    };
                })
            }))
            .filter((i: IAttribute) => !!i?.attributes?.find(attribute => attribute && !!attribute.values.find(value => !!value)));

        return sections.filter((i: IAttribute) => !!i);
    }

    private getValuesLabels(values: (string | number)[], attributeCode: string): string {
        const attribute = this.attributeResource.getExisting(attributeCode);
        const valuesLabels = values.map(value => {
            const labelValue = optionLabelPipe(value, attributeCode);

            return this.sanitizeHtmlService.html(`${labelValue}`);
        });

        if (!attribute?.data?.options) {
            return valuesLabels.join(',');
        } else if (valuesLabels.length) {
            return `${valuesLabels.map(i => `<div class="list-item">${i}</div>`).join('')}`;
        } else {
            return '';
        }
    }

    /**
     * Gets value
     */
    private getValues(code: string): (string | number)[] {
        const value = this.productViewLayout().product()?.data?.[code] || this.productViewLayout().parent()?.data?.[code];

        if (value) {
            return Array.isArray(value) ? value : [value];
        }

        return [];
    }
}
